import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { FilterClause, FilterPreviewResponse } from '../api/types';

interface Props {
  storeId: string;
  configName: string;
  filter: FilterClause | null;
}

export default function FilterPreview({ storeId, configName, filter }: Props) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ok'; data: FilterPreviewResponse }
    | { kind: 'error'; msg: string }
  >({ kind: 'idle' });
  const debounceTimer = useRef<number | null>(null);

  useEffect(() => {
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(async () => {
      setState((prev) => (prev.kind === 'idle' ? { kind: 'loading' } : prev));
      try {
        const data = await api.filterPreview(storeId, {
          config_name: configName,
          filter,
        });
        setState({ kind: 'ok', data });
      } catch (e) {
        setState({ kind: 'error', msg: (e as Error).message ?? 'unknown error' });
      }
    }, 500);
    return () => {
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    };
  }, [storeId, configName, filter]);

  return (
    <div
      className="rounded-2xl border border-ff-forest/30 bg-gradient-to-b from-ff-mint/40 to-ff-mint/10 p-3 mt-3"
      data-testid="filter-preview"
    >
      {state.kind === 'idle' && (
        <p className="text-[11px] font-mono text-ff-mute">computing match preview…</p>
      )}
      {state.kind === 'loading' && (
        <p className="text-[11px] font-mono text-ff-mute" data-testid="filter-preview-loading">
          updating…
        </p>
      )}
      {state.kind === 'error' && (
        <p className="text-[11px] font-mono text-ff-danger" data-testid="filter-preview-error">
          preview unavailable: {state.msg}
        </p>
      )}
      {state.kind === 'ok' && (
        <>
          <div className="flex items-baseline justify-between">
            <strong className="text-sm text-ff-forest" data-testid="filter-preview-count">
              {state.data.matched === state.data.total && filter === null
                ? `Catches all ${state.data.total} products`
                : `${state.data.matched} of ${state.data.total} products match`}
            </strong>
          </div>
          {state.data.samples.length > 0 && (
            <>
              <p className="font-mono text-[10px] uppercase tracking-widish text-ff-forest/70 mt-2 mb-1">
                Sample matches
              </p>
              <ul className="text-xs font-sans space-y-0.5" data-testid="filter-preview-samples">
                {state.data.samples.map((s) => (
                  <li key={s.id} className="flex gap-2 min-w-0">
                    <code className="text-ff-mute min-w-[60px] shrink-0">{s.id}</code>
                    <span className="truncate">{s.title}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {state.data.priority_conflict_count !== null &&
            state.data.priority_conflict_count > 0 && (
              <p
                className="text-[10px] font-mono text-ff-mute mt-2 italic"
                data-testid="filter-preview-priority-footnote"
              >
                Of these {state.data.matched} matches,{' '}
                {state.data.priority_conflict_count} may be claimed by a
                higher-priority config.
              </p>
            )}
        </>
      )}
    </div>
  );
}
